import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { ConfirmationModalIcon } from '@/shared/components/confirmation/ConfirmationModalIcon'
import {
  ConfirmationModalLayout,
  ConfirmationModalShell,
} from '@/shared/components/confirmation/ConfirmationModalShell'
import {
  confirmationBodySx,
  confirmationDangerButtonSx,
  confirmationHeadlineSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'

interface DeactivateProps {
  open: boolean
  tenantName: string
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function SensemakerDeactivateModal({
  open,
  tenantName,
  pending,
  onCancel,
  onConfirm,
}: DeactivateProps) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={pending ? undefined : onCancel}
      dialogTestId="sensemaker-deactivate-modal"
      ariaLabelledBy="sensemaker-deactivate-title"
      ariaDescribedBy="sensemaker-deactivate-body"
    >
      <ConfirmationModalLayout>
        <ConfirmationModalIcon variant="negative" testId="sensemaker-deactivate-icon" />

        <Box
          id="sensemaker-deactivate-body"
          sx={{
            width: '100%',
            px: '24px',
            pt: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography id="sensemaker-deactivate-title" component="h2" sx={confirmationHeadlineSx}>
            Are you sure you want to deactivate {tenantName}?
          </Typography>
          <Box sx={{ textAlign: 'center', maxWidth: 388, mx: 'auto' }}>
            <Typography sx={confirmationBodySx}>
              They will be unable to log in once deactivated.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            px: 3,
            py: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={pending}
            data-testid="sensemaker-deactivate-cancel"
            sx={confirmationSecondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={pending}
            data-testid="sensemaker-deactivate-confirm"
            sx={confirmationDangerButtonSx}
          >
            {pending ? <CircularProgress size={20} color="inherit" /> : 'Confirm Deactivate'}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}

interface ReactivateProps {
  open: boolean
  tenantName: string
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function SensemakerReactivateModal({
  open,
  tenantName,
  pending,
  onCancel,
  onConfirm,
}: ReactivateProps) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={pending ? undefined : onCancel}
      dialogTestId="sensemaker-reactivate-modal"
      ariaLabelledBy="sensemaker-reactivate-title"
      ariaDescribedBy="sensemaker-reactivate-body"
    >
      <ConfirmationModalLayout>
        <ConfirmationModalIcon variant="positive" testId="sensemaker-reactivate-icon" />

        <Box
          id="sensemaker-reactivate-body"
          sx={{
            width: '100%',
            px: '24px',
            pt: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography id="sensemaker-reactivate-title" component="h2" sx={confirmationHeadlineSx}>
            Are you sure you want to reactivate {tenantName}?
          </Typography>
          <Box sx={{ textAlign: 'center', maxWidth: 388, mx: 'auto' }}>
            <Typography sx={confirmationBodySx}>
              They will regain login access immediately.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            px: 3,
            py: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={pending}
            data-testid="sensemaker-reactivate-cancel"
            sx={confirmationSecondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={pending}
            data-testid="sensemaker-reactivate-confirm"
            sx={{
              ...confirmationDangerButtonSx,
              bgcolor: '#008236',
              '&:hover': { bgcolor: '#006b2d', boxShadow: 'none' },
            }}
          >
            {pending ? <CircularProgress size={20} color="inherit" /> : 'Confirm Reactivate'}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
